/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { ChangeDetectorRef, Component, Inject, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { Select, Store } from '@ngxs/store';
import { ReCaptchaV3Service, RecaptchaComponent } from 'ng-recaptcha-2';
import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { ACCOUNT_DATA_SERVICE, AccountDataService } from '../../../page-modules/account/services/account-data.service';
import { RedirectHelper } from '../../../page-modules/resource/store/editor/content/helpers/redirect.helper';
import { SnackbarHelper } from '../../../shared/helpers/snackbar-helper';
import { Organization } from '../../../shared/models';
import { MaintenanceService } from '../../../shared/services/maintenance/maintenance.service';
import { TriggerFolioUserLogin } from '../../store/user-auth.actions';
import { UserAuthState } from '../../store/user-auth.state';

@Component({
  templateUrl: './alumni-login.component.html',
  styleUrls: ['./alumni-login.component.scss'],
})
export class AlumniLoginComponent implements OnInit, OnDestroy {
  @Select(UserAuthState.organizationDetails)
  organizationData$: Observable<Organization>;

  @Select(UserAuthState.isLoginInProgress)
  loginInProgress$: Observable<boolean>;

  @ViewChild('captchaElem', { static: false }) captchaElem: RecaptchaComponent;

  loginFrom: FormGroup;
  forgotPasswordForm: FormGroup;
  showPasswordSelected: boolean;
  year: number;
  isForgotForm = false;
  forgotIsProgress: boolean;
  loginUrl: string;
  reCaptchaSiteKeyV2 = environment.reCaptchaSiteKeyV2;
  isBcuLogin: boolean;
  isCardiffLogin: boolean;
  isCoventryLogin: boolean;
  isLboroLogin: boolean;

  showMaintenanceOverlay: boolean;

  private organization: Organization;
  private organizationDataSubscription: Subscription;

  constructor(
    private store: Store,
    private fb: FormBuilder,
    private ngZone: NgZone,
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private translocoService: TranslocoService,
    @Inject(ACCOUNT_DATA_SERVICE) private accountService: AccountDataService,
    private cdr: ChangeDetectorRef,
    private captchaV3Service: ReCaptchaV3Service,
    private maintenanceService: MaintenanceService,
  ) {
    this.showMaintenanceOverlay = this.maintenanceService.showMaintenanceMessage();
  }

  ngOnInit() {
    this.organizationDataSubscription = this.organizationData$.pipe(filter((data) => !!data)).subscribe((data) => {
      this.organization = data;
      this.isLboroLogin = this.isLboroPage(this.organization?.domain);
      this.isBcuLogin = this.isBcuPage(this.organization?.domain);
      this.isCardiffLogin = this.isCardiffPage(this.organization?.domain);
      this.isCoventryLogin = this.isCoventryPage(this.organization?.domain);
    });

    this.year = new Date().getFullYear();
    this.forgotPasswordForm = this.fb.group({
      email: ['', Validators.compose([Validators.required, Validators.email])],
    });
    this.loginFrom = this.fb.group({
      email: ['', Validators.compose([Validators.required, Validators.email])],
      password: ['', Validators.required],
    });
    this.loginUrl = window.location.origin + '/signin';

    const userEmail = this.route.snapshot.queryParamMap.get('email');
    if (userEmail) {
      this.forgotPasswordForm.controls['email'].setValue(userEmail);
      this.loginFrom.controls['email'].setValue(userEmail);
    }
  }

  ngOnDestroy() {
    this.organizationDataSubscription?.unsubscribe();
  }

  onLoginUser() {
    if (this.loginFrom.valid && this.organization?._id) {
      this.store.dispatch(
        new TriggerFolioUserLogin(
          this.loginFrom.get('email').value?.trim(),
          this.loginFrom.get('password').value?.trim(),
          this.organization._id,
        ),
      );
    }
  }

  onForgotSubmit(captchaResponseV2: string) {
    if (this.forgotPasswordForm.valid) {
      this.forgotIsProgress = true;
      const email = this.forgotPasswordForm.get('email').value?.trim();
      this.captchaV3Service.execute('forgotPassword').subscribe((captchaResponseV3: string) => {
        this.accountService.resetPasswordByEmail(email, captchaResponseV3, captchaResponseV2).subscribe(({ isSuccess, error }) => {
          this.forgotIsProgress = false;
          if (isSuccess) {
            SnackbarHelper.showTranslatableSnackBar(
              this.ngZone,
              this.snackBar,
              this.translocoService,
              'translations.resetPasswordInstructionSend',
            );
          } else {
            SnackbarHelper.showSnackBar(this.ngZone, this.snackBar, error);
          }
          this.captchaElem.reset();
          this.cdr.detectChanges();
        });
      });
    }
  }

  onBackClicked() {
    this.isForgotForm = false;
  }

  onDescriptionClick(event: MouseEvent) {
    event.preventDefault();
    const target = event.target as HTMLElement;
    if (target.closest('.f_forgot') !== null) {
      this.onForgotPassword();
    } else if (target.closest('.f_saml') !== null) {
      RedirectHelper.redirectByUrl(this.ngZone, this.router, this.route, 'signin');
    } else if (target.closest('.f_singin') !== null) {
      RedirectHelper.redirectByUrl(this.ngZone, this.router, this.route, 'signin');
    } else if (target.closest('.f_mail') !== null) {
      const linkHref = (target as HTMLLinkElement)?.href;
      if (linkHref) {
        window.location.href = linkHref;
      }
    } else if (target.closest('.f_redirect') !== null) {
      const linkHref = (target as HTMLLinkElement)?.href;
      if (linkHref) {
        window.open(linkHref, '_blank');
      }
    }
  }

  onForgotPassword() {
    this.isForgotForm = true;
  }

  proceedIfValid(captchaResponseV2: string | null) {
    if (!captchaResponseV2) return;
    this.onForgotSubmit(captchaResponseV2);
  }

  sendRecoveryRequest() {
    if (this.forgotPasswordForm.valid) {
      this.captchaElem.execute();
    }
  }

  private isLboroPage(domain: string) {
    if (!domain) {
      return false;
    }
    return ['lboro.yuna.dev.potential.ly', 'lboro.potential.ly'].includes(domain);
  }

  private isBcuPage(domain: string) {
    if (!domain) {
      return false;
    }
    return ['bcu.yuna.dev.potential.ly', 'bcu.potential.ly'].includes(domain);
  }

  private isCardiffPage(domain: string) {
    if (!domain) {
      return false;
    }
    return ['cardiff.yuna.dev.potential.ly', 'cardiff.potential.ly'].includes(domain);
  }

  private isCoventryPage(domain: string) {
    if (!domain) {
      return false;
    }
    return ['coventry.yuna.dev.potential.ly', 'coventry.potential.ly'].includes(domain);
  }
}
